import { Box, Option, Select, Tab, TabList, Tooltip } from '@mui/joy';
import type { Icon } from '@phosphor-icons/react';

interface Section<Value extends string | number> {
  key: string;
  value: Value;
  title: string;
  icon: Icon;
}

export function DetailSectionNav<Value extends string | number>({ active, onChange, sections, entity }: {
  active: Value; onChange: (value: Value) => void; sections: Section<Value>[]; entity: string;
}) {
  return (
    <Box component="nav" aria-label={`${entity} sections`} sx={{ minWidth: 0, alignSelf: 'start', position: { md: 'sticky' }, top: 88 }}>
      <TabList aria-label={`${entity} sections`} disableUnderline sx={{
        display: { xs: 'none', md: 'flex' }, width: 64, boxSizing: 'border-box', p: 1, gap: 0.5,
        border: '1px solid', borderColor: 'divider', borderRadius: 'lg', bgcolor: 'background.surface',
        maxHeight: 'calc(100dvh - 112px)', overflowY: 'auto', scrollbarWidth: 'thin',
      }}>
        {sections.map(({ key, value, title, icon: Icon }) => (
          <Tooltip key={key} title={title} placement="right" arrow>
            {/* Joy Tab consumes onFocus; this wrapper forwards bubbling focus to the tooltip. */}
            <Box component="span" sx={{ display: 'flex' }}>
              <Tab value={value} aria-label={title} disableIndicator sx={{
                minHeight: 44, width: 44, p: 0, flexShrink: 0, borderRadius: 'md', color: 'text.secondary',
                '&.Mui-selected': { bgcolor: 'primary.softBg', color: 'primary.softColor', boxShadow: 'inset 3px 0 0 var(--joy-palette-primary-500)' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.500', outlineOffset: -2 },
              }}>
                <Icon size={22} weight={active === value ? 'fill' : 'regular'} aria-hidden="true" />
              </Tab>
            </Box>
          </Tooltip>
        ))}
      </TabList>
      <Select aria-label={`${entity} section`} value={active} onChange={(_, value) => value !== null && onChange(value)}
        sx={{ display: { xs: 'flex', md: 'none' }, minHeight: 44, width: '100%' }}>
        {sections.map(({ key, value, title, icon: Icon }) => (
          <Option key={key} value={value}><Icon size={20} aria-hidden="true" />{title}</Option>
        ))}
      </Select>
    </Box>
  );
}
